"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { io, Socket } from "socket.io-client";

const ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
  { urls: "stun:stun2.l.google.com:19302" },
  { urls: "stun:stun3.l.google.com:19302" },
  { urls: "stun:stun4.l.google.com:19302" },
];

interface VoiceChatState {
  isConnected: boolean;
  isInVoice: boolean;
  isMuted: boolean;
  isDeafened: boolean;
  participants: Array<{socketId: string; odId: string}>;
  currentChannel: string | null;
}

export function useVoiceChat(userId: string) {
  const socketRef = useRef<Socket | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const peersRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const audioElementsRef = useRef<Map<string, HTMLAudioElement>>(new Map());

  const [state, setState] = useState<VoiceChatState>({
    isConnected: false,
    isInVoice: false,
    isMuted: false,
    isDeafened: false,
    participants: [],
    currentChannel: null,
  });

  useEffect(() => {
    const socket = io({ path: "/socket.io", transports: ["websocket", "polling"] });
    socketRef.current = socket;

    socket.on("connect", () => {
      setState(s => ({ ...s, isConnected: true }));
    });

    socket.on("disconnect", () => {
      setState(s => ({ ...s, isConnected: false }));
    });

    socket.on("existing-users", async (users: Array<{socketId: string, userId: string}>) => {
      for (const user of users) {
        await createPeerConnection(user.socketId, true);
      }
      setState(s => ({ ...s, participants: users.map(u => ({ socketId: u.socketId, odId: u.userId })) }));
    });

    socket.on("user-joined", async ({ socketId, userId }: { socketId: string; userId: string }) => {
      await createPeerConnection(socketId, false);
      setState(s => ({ ...s, participants: [...s.participants, { socketId, odId: userId }] }));
    });

    socket.on("user-left", ({ socketId }: { socketId: string }) => {
      closePeerConnection(socketId);
      setState(s => ({ ...s, participants: s.participants.filter(p => p.socketId !== socketId) }));
    });

    socket.on("offer", async ({ from, offer }: { from: string; offer: RTCSessionDescriptionInit }) => {
      const pc = peersRef.current.get(from) || await createPeerConnection(from, false);
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      socket.emit("answer", { to: from, answer });
    });

    socket.on("answer", async ({ from, answer }: { from: string; answer: RTCSessionDescriptionInit }) => {
      const pc = peersRef.current.get(from);
      if (pc) await pc.setRemoteDescription(new RTCSessionDescription(answer));
    });

    socket.on("ice-candidate", async ({ from, candidate }: { from: string; candidate: RTCIceCandidateInit }) => {
      const pc = peersRef.current.get(from);
      if (pc && candidate) await pc.addIceCandidate(new RTCIceCandidate(candidate));
    });

    return () => {
      socket.disconnect();
      localStreamRef.current?.getTracks().forEach(t => t.stop());
      peersRef.current.forEach(pc => pc.close());
      audioElementsRef.current.forEach(el => el.remove());
    };
  }, []);

  const createPeerConnection = async (peerId: string, initiator: boolean) => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    peersRef.current.set(peerId, pc);

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current!);
      });
    }

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        socketRef.current?.emit("ice-candidate", { to: peerId, candidate: e.candidate });
      }
    };

    pc.ontrack = (e) => {
      let audio = audioElementsRef.current.get(peerId);
      if (!audio) {
        audio = document.createElement("audio");
        audio.autoplay = true;
        audioElementsRef.current.set(peerId, audio);
      }
      audio.srcObject = e.streams[0];
    };

    if (initiator) {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socketRef.current?.emit("offer", { to: peerId, offer });
    }

    return pc;
  };

  const closePeerConnection = (peerId: string) => {
    peersRef.current.get(peerId)?.close();
    peersRef.current.delete(peerId);
    audioElementsRef.current.get(peerId)?.remove();
    audioElementsRef.current.delete(peerId);
  };

  const joinVoice = useCallback(async (channelId: string) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;
      socketRef.current?.emit("join-voice", { channelId, userId });
      setState(s => ({ ...s, isInVoice: true, currentChannel: channelId }));
    } catch (err) {
      console.error("Failed to get microphone:", err);
      alert("Mikrofon erişimi reddedildi");
    }
  }, [userId]);

  const leaveVoice = useCallback(() => {
    if (state.currentChannel) {
      socketRef.current?.emit("leave-voice", { channelId: state.currentChannel });
    }
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    localStreamRef.current = null;
    peersRef.current.forEach(pc => pc.close());
    peersRef.current.clear();
    audioElementsRef.current.forEach(el => el.remove());
    audioElementsRef.current.clear();
    setState(s => ({ ...s, isInVoice: false, currentChannel: null, participants: [] }));
  }, [state.currentChannel]);

  const toggleMute = useCallback(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setState(s => ({ ...s, isMuted: !audioTrack.enabled }));
      }
    }
  }, []);

  const toggleDeafen = useCallback(() => {
    audioElementsRef.current.forEach(audio => {
      audio.muted = !state.isDeafened;
    });
    setState(s => ({ ...s, isDeafened: !s.isDeafened }));
  }, [state.isDeafened]);

  return { ...state, joinVoice, leaveVoice, toggleMute, toggleDeafen };
}
